import { Checkbox, Intent } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import { BroodRequestApi, BroodRequestGridDto, BroodRequestSearch, ERequestState } from "../../../../api";
import { AddButton, EditButton, SearchTablePage, ViewButton, CloseButton } from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface IBroodRequestListPageProps {}

export const BroodRequestListPage: React.FunctionComponent<IBroodRequestListPageProps> = (props) => {
  const api = useApiService(BroodRequestApi);
  const [selectAll, setselectAll] = React.useState(false);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const { dispatchEvent } = useEventsContext();
  const closableIdsList = React.useMemo(() => [], []);
  const selectionList = React.useMemo(() => [], []);
  const { showDialog } = useDialog();

  const columnCheckBox = React.useCallback(
    (row: BroodRequestGridDto) => {
      const disabled = row.requestStateCode !== ERequestState.PAID;
      if (!disabled) {
        closableIdsList.push(row.requestId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
          disabled={disabled}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: BroodRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.broodRequest}/${row.broodRequestId}/detail/view`)}
            />

            <EditButton
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              minimal={true}
              onClick={() => history.push(`${ERoutes.broodRequest}/${row.broodRequestId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: BroodRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.IsBrcsRequest),
        fieldName: "isBrcsRequest",
        alignment: "center",
        render: (row: BroodRequestGridDto) => <Checkbox checked={row.isBrcsRequest} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Mother),
        fieldName: "mother",
        render: (row: BroodRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.motherId}/detail/view`}>
            {row.mother}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: BroodRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.ProtusionDate),
        fieldName: "protrusionDate",
      },
      {
        header: () => t(ETLCodes.BirthDate),
        fieldName: "birthDate",
      },
      {
        header: () => t(ETLCodes.NumberOfPuppies),
        fieldName: "numberOfPuppies",
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [columnCheckBox, history, t, toggleSelectAll]
  );

  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              await requestList.forEach((element) => {
                api.closeRequest(element);
              });
              dispatchEvent("RefreshSearchTables", 0);
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
          },
        });
      }
    },
    [api, dispatchEvent, selectionList.length, showDialog, t]
  );
  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.broodRequest}/0/detail/edit`);
  }, [history]);
  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        BroodRequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    []
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          BroodRequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.BroodRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
        </div>
      }
    />
  );
};
